import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { ActivityActor } from '@fintree/supabase/shared'
import { formatDisplayDate } from '@fintree/utils'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useAuth } from '@/hooks/useAuth'
import { addStaffSchema, resolveStaffRoleSelectValue, type AddStaffForm } from './settingsTypes'
import {
  useCreateStaffMember,
  useDeactivateStaff,
  useUpdateStaffCustomRole,
} from '@/hooks/mutations/useStaffRoleMutations'
import { useCustomRoles, useStaffMembers } from '@/hooks/queries/useStaffQueries'

interface StaffTabProps {
  actor: ActivityActor
}

export function StaffTab({ actor }: StaffTabProps) {
  const { identity } = useAuth()
  const [addOpen, setAddOpen] = useState(false)
  const [tempPasswordResult, setTempPasswordResult] = useState<{ email: string; password: string } | null>(null)
  const [deactivateId, setDeactivateId] = useState<string | null>(null)

  const { data: roles = [] } = useCustomRoles()
  const staffQuery = useStaffMembers()

  const addForm = useForm<AddStaffForm>({
    resolver: zodResolver(addStaffSchema),
    defaultValues: { name: '', email: '', customRoleId: '' },
  })

  const assignableRoles = roles.filter((r) => r.name !== 'super_admin')

  const createStaffMut = useCreateStaffMember(actor, {
    onSuccess: (result) => {
      setAddOpen(false)
      addForm.reset()
      setTempPasswordResult({ email: result.staff.email, password: result.tempPassword })
    },
  })

  const staffRoleMut = useUpdateStaffCustomRole(actor)

  const deactivateMut = useDeactivateStaff(actor, {
    onSuccess: () => setDeactivateId(null),
  })

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Staff members" subtitle="Accounts and role assignment." />
        <Button type="button" onClick={() => setAddOpen(true)}>
          + Add staff member
        </Button>
      </div>

      {staffQuery.isLoading ? (
        <LoadingSpinner />
      ) : staffQuery.isError ? (
        <div className="rounded-lg border border-destructive/ bg-destructive/ p-4 text-sm text-destructive">Unable to load staff.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(staffQuery.data ?? []).map((row) => {
                const isSelf = row.id === identity?.staffId
                const inactive = row.active === false
                const effectiveRoleId = resolveStaffRoleSelectValue(row, roles)
                return (
                  <tr key={row.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium text-foreground">{row.name ?? '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.email}</td>
                    <td className="px-3 py-2">
                      <select
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                        value={effectiveRoleId}
                        disabled={inactive || staffRoleMut.isPending || row.role === 'super_admin'}
                        onChange={(e) => {
                          const next = e.target.value
                          if (!next || next === effectiveRoleId) return
                          staffRoleMut.mutate({ id: row.id, customRoleId: next })
                        }}
                      >
                        <option value="">Select role</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                            {r.description ? ` — ${r.description}` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{inactive ? 'Inactive' : 'Active'}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {row.created_at ? formatDisplayDate(row.created_at) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {!inactive && !isSelf && row.role !== 'super_admin' ? (
                        <Button type="button" variant="outline" size="sm" onClick={() => setDeactivateId(row.id)}>
                          Deactivate
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add staff member</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3 py-2"
            onSubmit={addForm.handleSubmit((values) => createStaffMut.mutate(values))}
          >
            <div>
              <Label>Name</Label>
              <Input className="mt-1" disabled={createStaffMut.isPending} {...addForm.register('name')} />
              {addForm.formState.errors.name ? (
                <p className="mt-1 text-xs text-destructive">{addForm.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div>
              <Label>Email</Label>
              <Input className="mt-1" type="email" disabled={createStaffMut.isPending} {...addForm.register('email')} />
              {addForm.formState.errors.email ? (
                <p className="mt-1 text-xs text-destructive">{addForm.formState.errors.email.message}</p>
              ) : null}
            </div>
            <div>
              <Label>Role</Label>
              <Controller
                name="customRoleId"
                control={addForm.control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    disabled={createStaffMut.isPending}
                  >
                    <option value="">Select a role</option>
                    {assignableRoles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} — {r.description ?? ''}
                      </option>
                    ))}
                  </select>
                )}
              />
              {addForm.formState.errors.customRoleId ? (
                <p className="mt-1 text-xs text-destructive">{addForm.formState.errors.customRoleId.message}</p>
              ) : null}
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createStaffMut.isPending}>
                {createStaffMut.isPending ? 'Creating…' : 'Create account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(tempPasswordResult)} onOpenChange={(o) => !o && setTempPasswordResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Temporary password</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Share this one-time password with <strong className="text-foreground">{tempPasswordResult?.email}</strong>.
            They should sign in and change their password immediately.
          </p>
          <div className="rounded-md border border-border bg-muted/40 p-3 font-mono text-sm break-all">
            {tempPasswordResult?.password}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                if (tempPasswordResult?.password) {
                  await navigator.clipboard.writeText(tempPasswordResult.password)
                  toast.success('Copied to clipboard')
                }
              }}
            >
              Copy password
            </Button>
            <Button type="button" onClick={() => setTempPasswordResult(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deactivateId)} onOpenChange={(o) => !o && setDeactivateId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate staff?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">They will no longer be able to sign in. You can re-activate later in the database if needed.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeactivateId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deactivateMut.isPending}
              onClick={() => deactivateId && deactivateMut.mutate(deactivateId)}
            >
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
